import {
  Box,
  Container,
  Hidden,
  styled,
  Theme,
  alpha,
  Typography
} from "@material-ui/core";
import { motion, Variants } from "framer-motion";
import React from "react";
import { getDefaultProductVariant } from "../../../../../helpers/product";
import { ProductType } from "../../../../../types/airgraft";
import { useExpressDeliveryAvailabilitySelector } from "../../../../../hooks/use-express-delivery-availability";
import ProductQuantitySelector from "../../../ProductQuantitySelector";
import { PRODUCT_DETAILS_ACTIONS_MAX_WIDTH } from "../Actions/styles";
import useFeatureFlag from "../../../../../hooks/use-feature-flag";
import { useProductVariantByQueryParam } from "../../utils";

export const MotionWrapper = styled(motion.div)(
  ({ theme, isModal }: { theme: Theme; isModal: boolean }) => ({
    position: isModal ? "absolute" : "fixed",
    top: 0,
    left: 0,
    width: "100%",
    minHeight: "56px",
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    borderTopLeftRadius: isModal ? "12px" : "0px",
    borderTopRightRadius: isModal ? "12px" : "0px",
    zIndex: 100,
    whiteSpace: "nowrap",
    overflow: "hidden",
    maxWidth: "100%",
    backgroundColor: alpha(theme.palette.background.default, 0.9),
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    [theme.breakpoints.up("md")]: {
      minHeight: "76px",
      // Show sticky bar at the bottom for standalone pdp
      top: isModal ? 0 : "auto",
      bottom: isModal ? "auto" : 0
    }
  })
);

const variants: Variants = {
  initial: {
    opacity: 0
  },
  visible: {
    y: "0%",
    transition: { duration: 0.25 },
    opacity: 1
  },
  exit: {
    transition: { duration: 0.15 },
    opacity: 0
  }
};

type Props = {
  product: ProductType;
  isVisible: boolean;
  isModal?: boolean;
};

export default function ProductDetailsStickyBar({
  product,
  isVisible,
  isModal
}: Props) {
  const isXpressEnabled = useFeatureFlag("SHOP_XPRESS");
  const [productVariant] = useProductVariantByQueryParam(product);

  const getProductExpressAvailability = useExpressDeliveryAvailabilitySelector(
    c => c.getProductExpressAvailability
  );
  const getAvailableProviderProductData =
    useExpressDeliveryAvailabilitySelector(
      c => c.getAvailableProviderProductData
    );

  const isAvailableForDelivery =
    getProductExpressAvailability(product) === "AVAILABLE";

  return (
    <MotionWrapper
      initial={"initial"}
      animate={isVisible ? "visible" : "exit"}
      variants={variants}
      isModal={isModal}
      className={!isModal ? "mui-fixed" : undefined} // Prevent bar from moving left when a modal is opened
    >
      <Container maxWidth="md">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" component="span">
              {productVariant.title}
            </Typography>
            <Typography
              variant="h6"
              component="span"
              color="textSecondary"
            >{` by `}</Typography>
            <Typography variant="h6" component="span">
              {productVariant.brand?.name}
            </Typography>
          </Box>

          {isXpressEnabled && isAvailableForDelivery && (
            <Hidden smDown>
              <Box flex={1} maxWidth={PRODUCT_DETAILS_ACTIONS_MAX_WIDTH}>
                <ProductQuantitySelector
                  product={product}
                  maxQuantity={
                    getAvailableProviderProductData(product)
                      ?.availableQuantity || 0
                  }
                />
              </Box>
            </Hidden>
          )}
        </Box>
      </Container>
    </MotionWrapper>
  );
}
